import { iosCartBoldWeight } from 'features/cart/styles';
import { ICartEntry } from 'features/cart/types';
import { calculateEntryTotalPrice } from 'features/cart/utils';
import { StyleSheet, View } from 'react-native';

import { Typography } from 'components/ui';
import { Skeleton } from 'components/ui/skeleton';
import { useIsLargeFontScale } from 'hooks/use-is-large-font-scale';
import { t } from 'state/intl/translations';
import { useUIContext } from 'state/ui';
import { primitive } from 'styles/constants';

import { CartEntryDeleteButton } from '../cart-actions/cart-entry-delete-button';
import { CartEntryDuplicateButton } from '../cart-actions/cart-entry-duplicate-button';
import { CartEntryEditButton } from '../cart-actions/cart-entry-edit-button';
import { CartEntryRewardActionButton } from '../cart-actions/cart-entry-reward-action-button';

export const CartEntryFooter = ({
  cartEntry,
  isLoading,
  showPriceSkeleton = false,
  quantityOnCart,
}: {
  cartEntry: ICartEntry;
  isLoading?: boolean;
  showPriceSkeleton?: boolean;
  quantityOnCart?: number;
}) => {
  const { formatCurrencyForLocale } = useUIContext();
  const isLargeFontScale = useIsLargeFontScale();
  const entryTotalPrice = calculateEntryTotalPrice(cartEntry);
  const priceText = entryTotalPrice === 0 ? t('free') : formatCurrencyForLocale(entryTotalPrice);

  const price = showPriceSkeleton ? (
    <Skeleton shape="rectangle" style={styles.priceSkeleton} />
  ) : (
    <Typography style={styles.editText}>{priceText}</Typography>
  );

  return (
    <View
      testID="cart-entry-footer"
      style={[styles.footer, isLargeFontScale && styles.stackedFooter]}
    >
      <View style={styles.actionsRow}>
        <CartEntryDeleteButton cartEntry={cartEntry} disabled={isLoading} variant="iconOnly" />
        <CartEntryDuplicateButton
          cartEntry={cartEntry}
          disabled={isLoading}
          quantityOnCart={quantityOnCart}
        />
        <CartEntryEditButton cartEntry={cartEntry} disabled={isLoading} />
      </View>
      {isLargeFontScale ? null : <View style={styles.spacer} />}
      <View style={[styles.rewardPriceRow, isLargeFontScale && styles.stackedRewardPriceRow]}>
        <CartEntryRewardActionButton cartEntry={cartEntry} isLoading={!!isLoading} />
        {price}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  footer: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderTopWidth: 1,
    borderTopColor: primitive.$blackOpacity10,
    alignItems: 'center',
    gap: 16,
  },
  stackedFooter: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  actionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 16,
  },
  spacer: {
    flex: 1,
  },
  rewardPriceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  stackedRewardPriceRow: {
    justifyContent: 'space-between',
    width: '100%',
  },
  editText: {
    fontSize: 14,
    fontFamily: primitive.fontFamily.$headers,
    ...iosCartBoldWeight,
    color: primitive.$bbqBrown,
  },
  priceSkeleton: {
    width: 42,
    height: 18,
    marginVertical: 0,
  },
});
